import { m } from "@nerves/i18n/messages";
import { getLocale } from "@nerves/i18n/runtime";

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Compact age for dense rows: "now", "5m", "3h", "4d", "2w", then a short date. */
export function shortAge(iso: string, now = Date.now()): string {
  const d = Math.max(0, now - Date.parse(iso));
  if (d < MIN) return m.time_now();
  if (d < HOUR) return m.age_minutes({ n: Math.floor(d / MIN) });
  if (d < DAY) return m.age_hours({ n: Math.floor(d / HOUR) });
  if (d < 14 * DAY) return m.age_days({ n: Math.floor(d / DAY) });
  if (d < 8 * 7 * DAY) return m.age_weeks({ n: Math.floor(d / (7 * DAY)) });
  return shortDate(iso, now);
}

export function shortDate(iso: string, now = Date.now()): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat(getLocale(), {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
}

/** Full date and time for titles and accessible names. */
export function fullDateTime(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * DAY],
  ["month", 30 * DAY],
  ["week", 7 * DAY],
  ["day", DAY],
  ["hour", HOUR],
  ["minute", MIN],
];

/** "3 hours ago" / "vor 3 Stunden". */
export function relativeTime(iso: string, now = Date.now()): string {
  const diff = Date.parse(iso) - now;
  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return m.time_just_now();
}

export const isMacLike = () => /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
